import { GridColDef } from "@mui/x-data-grid";
import { useConselhosDispatch, useConselhosState } from "./conselhos-context";
import { ACTION_TYPES, initialDataRequest } from "./conselhos-reducer";
import AdminPortalAppService from "../../../../application/platform/admin-portal/admin-portal-app-service";
import { useBaseLayoutController } from "../../../base-layout/context/base-layout-controller";
import { IConselhoResponse } from "../../../../model/admin-portal/response/conselho-response";
import { ITermPaggedRequest } from "../../../../model/admin-portal/request/term-pagged-request";
import { IMembroConselhoRequest } from "../../../../model/admin-portal/request/membro-conselho-request";
import { IAtaConselhoRequest } from "../../../../model/admin-portal/request/ata-conselho-request";
import { IconButton, Box, Tooltip } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';

export const useConselhosController = () => {
    const state = useConselhosState();
    const dispatch = useConselhosDispatch();
    const { showMessage, showBackDrop } = useBaseLayoutController();

    const loadData = async (searchRequest: ITermPaggedRequest) => {
        try {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: true });
            const response = await AdminPortalAppService.listConselhos(searchRequest);
            dispatch({ type: ACTION_TYPES.setDataPaginatedListResponse, payload: response });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar conselhos' });
        } finally {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: false });
        }
    };

    const onChangePagination = async (page: number, pageSize: number) => {
        const newSearch = { ...state.dataSearchRequest, page, pageSize };
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: newSearch });
        await loadData(newSearch);
    };

    const handleOnChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: { ...state.dataSearchRequest, term: e.target.value } });
    };

    const clearFields = async () => {
        const newSearch = { page: 0, pageSize: state.dataSearchRequest.pageSize, term: '' };
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: newSearch });
        await loadData(newSearch);
    };

    const loadLookups = async () => {
        try {
            const areas = await AdminPortalAppService.listAreasTransparenciaSetorial();
            dispatch({ type: ACTION_TYPES.setAreasList, payload: areas });
        } catch (error: any) {
            showMessage({ type: 'error', message: 'Erro ao carregar lista de Áreas' });
        }
    };

    const getData = async (id: number) => {
        try {
            showBackDrop(true);
            const response = await AdminPortalAppService.getConselho(id);
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: response });
            dispatch({
                type: ACTION_TYPES.setDataRequest, payload: {
                    id: response.id,
                    idAreaTransparenciaSetorial: response.idAreaTransparenciaSetorial,
                    nomeConselho: response.nomeConselho,
                    textoNormaCriacao: response.textoNormaCriacao,
                    membros: response.membros?.map(m => ({ ...m })) || [],
                    atas: response.atas?.map(a => ({ id: a.id, descricao: a.descricao, dtaReuniao: a.dtaPublicacao, documentoUrl: a.documentoUrl })) || []
                }
            });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar conselho' });
        } finally {
            showBackDrop(false);
        }
    };

    const validFields = (): boolean => {
        const r = state.dataRequest;
        if (!r.idAreaTransparenciaSetorial) return false;
        if (!r.nomeConselho || r.nomeConselho.trim() === '') return false;
        return true;
    };

    const handleOnChangeForm = (field: string, value: any) => {
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, [field]: value } });
    };

    const openForm = (row?: IConselhoResponse) => {
        if (row) {
            getData(row.id);
        } else {
            dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialDataRequest });
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
        }
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: true });
    };

    const closeForm = () => {
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: false });
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialDataRequest });
        dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
    };

    const save = async () => {
        if (!validFields()) {
            showMessage({ type: 'warning', message: 'Preencha todos os campos obrigatórios' });
            return;
        }

        try {
            showBackDrop(true);
            await AdminPortalAppService.addUpdateConselho(state.dataRequest);
            showMessage({
                type: 'success',
                message: state.dataRequest.id !== undefined && state.dataRequest.id !== null ? 'Conselho atualizado com sucesso' : 'Conselho criado com sucesso'
            });
            closeForm();
            await loadData(state.dataSearchRequest);
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao salvar conselho' });
        } finally {
            showBackDrop(false);
        }
    };

    const saveMembro = (membro: IMembroConselhoRequest, index?: number) => {
        const membros = [...state.dataRequest.membros];
        if (index !== undefined) membros[index] = membro; else membros.push(membro);
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, membros } });
    };

    const removeMembro = (index: number) => {
        const membros = state.dataRequest.membros.filter((_, i) => i !== index);
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, membros } });
    };

    const saveAta = (ata: IAtaConselhoRequest, index?: number) => {
        const atas = [...state.dataRequest.atas];
        if (index !== undefined) atas[index] = ata; else atas.push(ata);
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, atas } });
    };

    const removeAta = (index: number) => {
        const atas = state.dataRequest.atas.filter((_, i) => i !== index);
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, atas } });
    };

    const getColumnsGrid = (): GridColDef[] => {
        return [
            { field: 'areaName', headerName: 'Área', width: 150 },
            { field: 'nomeConselho', headerName: 'Nome do Conselho', flex: 1.5, minWidth: 250 },
            { field: 'textoNormaCriacao', headerName: 'Norma de Criação', flex: 1, minWidth: 200 },
            {
                field: 'actions', headerName: 'Ações', width: 90, align: 'center', headerAlign: 'center', sortable: false,
                renderCell: (params) => (
                    <Box display="flex" gap={1}>
                        <Tooltip title="Editar">
                            <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openForm(params.row); }}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            }
        ];
    };

    return {
        ...state,
        loadData,
        onChangePagination,
        handleOnChangeSearch,
        clearFields,
        loadLookups,
        getData,
        validFields,
        handleOnChangeForm,
        openForm,
        closeForm,
        save,
        saveMembro,
        removeMembro,
        saveAta,
        removeAta,
        getColumnsGrid
    };
};
